import { useState, useMemo } from "react";
import {
  Box, Typography, Chip, Divider,
  List, ListItem, Tooltip, IconButton,
} from "@mui/material";
import { Info, Check } from "lucide-react";
import { useTheme, alpha } from "@mui/material/styles";
import { useTranslation } from "../../../../language";
import { Gauge } from "@mui/x-charts/Gauge";
import TextInfoComp from "./TextInfoComp";
import CustomDialogSmall from "../../../../components/Dialog/DiaCompSmall";

export default function InflammatoryPathwaysCard({ lps, accentColor }) {
  const theme = useTheme();
  const { t } = useTranslation();

  const [openInfo, setOpenInfo] = useState(false);
  const [selectedMarker, setSelectedMarker] = useState(null);

  if (!Array.isArray(lps) || lps.length === 0) {
    return <Typography>No data available</Typography>;
  }

  const { normalCount, abnormalCount, total } = useMemo(() => {
    const result = lps.reduce(
      (acc, m) => {
        if (m.evaluation === "N") acc.normalCount++;
        if (m.evaluation === "A") acc.abnormalCount++;
        return acc;
      },
      { normalCount: 0, abnormalCount: 0 }
    );
    return { ...result, total: lps.length };
  }, [lps]);

  const activeCount = abnormalCount;

  const severityText =
    activeCount >= 3 ? t("inflam_level_high")
    : activeCount === 2 ? t("inflam_level_moderate")
    : t("inflam_level_low");

  const severityBody =
    activeCount >= 3 ? t("inflam_result_body_high")
    : activeCount === 2 ? t("inflam_result_body_moderate")
    : t("inflam_result_body_low");

  return (
    <>
      <Box sx={{
        borderRadius: "16px !important",
        overflow: "hidden",
        p: { xs: 2, sm: 2.5, md: 3 },
        bgcolor: theme.palette.mode === "dark" ? alpha("#fff", 0.04) : alpha("#fff", 0.5),
        backdropFilter: "blur(28px) saturate(180%)",
        WebkitBackdropFilter: "blur(28px) saturate(180%)",
        border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.65)"}`,
        boxShadow: theme.palette.mode === "dark"
          ? "inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)"
          : "inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)",
      }}>

        {/* Header */}
        <Typography variant="overline" sx={{ mb: 0.5 }}>
          {t("inflam_overline")}
        </Typography>
        <Typography variant="h6" fontWeight={500}>
          {t("inflam_title")}
        </Typography>

        <Divider sx={{ my: 1.5 }} />

        {/* Description */}
        <Typography variant="overline" sx={{ mb: 0.75 }}>
          {t("inflam_what_label")}
        </Typography>
        <Typography variant="subtitle2" sx={{ lineHeight: 1.65 }}>
          {t("inflam_what_body")}
        </Typography>

        <Divider sx={{ my: 1.5 }} />

        {/* Result */}
        <Typography variant="overline" sx={{ mb: 1 }}>
          {t("inflam_result_label")}
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Gauge
            value={normalCount}
            valueMax={total}
            text={({ value, valueMax }) => `${value} / ${valueMax}`}
            sx={{
              width: 72, height: 72, flexShrink: 0,
              [`& .MuiGauge-valueArc`]: { fill: theme.palette.traffic.green },
              [`& .MuiGauge-referenceArc`]: { fill: theme.palette.traffic.orange },
            }}
          />
          <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", minHeight: 72 }}>
            <Typography variant="subtitle1" fontWeight={500} lineHeight={1.2} gutterBottom>
              {severityText}
            </Typography>
            <Typography variant="subtitle2" sx={{ lineHeight: 1.4 }}>
              {severityBody}
            </Typography>
          </Box>
        </Box>

        {/* List */}
        <List disablePadding>
          {lps.map((m, index) => (
            <ListItem
              key={m.id ?? index}
              divider
              disableGutters
              sx={{ py: 0.5, minHeight: 36 }}
            >
              <Box sx={{ display: "flex", alignItems: "center", width: "100%", justifyContent: "space-between" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                  {m.marker?.name_key ? t(m.marker.name_key) : "Unknown"}
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Tooltip title="Info">
                    <IconButton size="small" sx={{ pr: 3 }}
                      onClick={(e) => { e.stopPropagation(); setSelectedMarker(m); setOpenInfo(true); }}
                    >
                      <Info size={18} />
                    </IconButton>
                  </Tooltip>

                  <Box sx={{ minWidth: 90, display: "flex", justifyContent: "flex-end" }}>
                    {m.evaluation === "N" ? (
                      <Check size={18} strokeWidth={3} color={theme.palette.traffic.green} />
                    ) : m.evaluation === "A" ? (
                      <Chip
                        label="ABNORMAL"
                        size="small"
                        sx={{
                          bgcolor: theme.palette.traffic.orange, color: "#fff",
                          fontWeight: 700, fontSize: "0.65rem", height: 22,
                          borderRadius: "6px", "& .MuiChip-label": { px: 1 },
                        }}
                      />
                    ) : null}
                  </Box>
                </Box>
              </Box>
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Dialog */}
      <CustomDialogSmall
        open={openInfo}
        onClose={() => setOpenInfo(false)}
        title={t("gut_marker_info")}
        content={
          <TextInfoComp
            description={selectedMarker?.marker?.marker_info_key ? t(selectedMarker.marker.marker_info_key, selectedMarker.marker.marker_info) : ""}
            lowtext={selectedMarker?.marker?.marker_info_lowtext_key ? t(selectedMarker.marker.marker_info_lowtext_key) : ""}
            lowrec={selectedMarker?.marker?.marker_info_lowrec_key ? t(selectedMarker.marker.marker_info_lowrec_key) : ""}
            rangetext={selectedMarker?.marker?.marker_info_rangetext_key ? t(selectedMarker.marker.marker_info_rangetext_key) : ""}
            hightext={selectedMarker?.marker?.marker_info_hightext_key ? t(selectedMarker.marker.marker_info_hightext_key) : ""}
            highrec={selectedMarker?.marker?.marker_info_highrec_key ? t(selectedMarker.marker.marker_info_highrec_key) : ""}
          />
        }
      />
    </>
  );
}
